import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import { Autocomplete, Button, TextField, FormControl } from '@mui/material'
import { FC, useMemo, useState } from 'react'
import { Controller } from 'react-hook-form'
import { IMUIAutoCompleteSingleSelect } from '../../../Models'
import ChipButton from '../ChipButton'
import { transform } from '../../../Helpers/Util'
import useApiPaginatedOptions from '../../../Hooks/useApiPaginatedOptions'
import useDebounce from '../../../Hooks/useDebounce'
import LazyLoader from '../Loader/LazyLoader'

const MUIAutocompleteSingleSelect: FC<IMUIAutoCompleteSingleSelect> = ({
  options = [],
  error,
  label,
  name,
  control,
  rules,
  labelKey = 'label',
  valueKey = 'value',
  required = false,
  disabled = false,
  defaultValueIndex = Number.NaN,
  startIcon,
  searchOnly,
  placeholder,
  onChange,
  statusKey,
  statusLabel,
  transformToBoolean = false,
  enableLazyLoad = false,
  fetchPage,
  pageSize = 20,
  searchDebounceMs = 400,
  scrollThresholdPx = 60,
  selectedOption,
}) => {
  const [open, setOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const debouncedSearch = useDebounce(inputValue, searchDebounceMs)
  const noopFetch = useMemo<NonNullable<typeof fetchPage>>(
    () => async () => ({ data: [], total: 0 }),
    [],
  )
  const {
    options: lazyOptions,
    isLoading: lazyLoading,
    isFetchingMore: lazyFetchingMore,
    hasMore: lazyHasMore,
    error: lazyError,
    loadMore: lazyLoadMore,
    retry: lazyRetry,
  } = useApiPaginatedOptions<any>({
    fetchPage: fetchPage ?? noopFetch,
    getOptionId: (opt) => opt?.[valueKey],
    pageSize,
    searchText: debouncedSearch,
    enabled: enableLazyLoad && open,
    logContext: `singleselect:${name}`,
  })

  const lazyMergedOptions = useMemo(() => {
    if (inputValue.trim()) return lazyOptions
    const isLoaded = lazyOptions.some((opt: any) => opt?.[valueKey] === selectedOption?.[valueKey])
    if (selectedOption && selectedOption[valueKey] !== undefined && !isLoaded) {
      return [...lazyOptions, selectedOption]
    }

    return lazyOptions
  }, [selectedOption, lazyOptions, valueKey, inputValue])

  const handleLazyScroll = (event: React.UIEvent<HTMLUListElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = event.currentTarget
    if (
      scrollHeight - scrollTop - clientHeight <= scrollThresholdPx &&
      lazyHasMore &&
      !lazyFetchingMore &&
      !lazyLoading &&
      !lazyError
    ) {
      lazyLoadMore()
    }
  }

  const lazyNoOptionsText = (() => {
    if (lazyLoading) {
      return (
        <span data-testid='initial-loader'>
          <LazyLoader />
        </span>
      )
    }
    if (lazyError) {
      return (
        <span
          data-testid='error-state'
          style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}
        >
          Failed to load options
          <Button
            size='small'
            variant='outlined'
            data-testid='retry-button'
            onMouseDown={(e) => e.preventDefault()}
            onClick={lazyRetry}
          >
            Retry
          </Button>
        </span>
      )
    }
    return <span data-testid='empty-state'>No options</span>
  })()
  const formControlStyle: {
    textAlign: string
    position: string
    backgroundColor?: string
    borderRadius?: string
  } = { textAlign: 'left', position: 'relative' }

  if (name === 'parameterSearch') {
    formControlStyle['backgroundColor'] = 'white'
    formControlStyle['borderRadius'] = '5px'
  }

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field }) => {
        const textFieldStyle: {
          '& .MuiInputBase-root'?: { fontWeight?: string; borderRadius?: string }
        } = {}

        if (field.value && searchOnly) {
          textFieldStyle['& .MuiInputBase-root'] = {
            fontWeight: '600',
          }
        }

        if (name === 'parameterSearch') {
          textFieldStyle['& .MuiInputBase-root'] = {
            ...textFieldStyle['& .MuiInputBase-root'],
            borderRadius: formControlStyle.borderRadius,
          }
        }

        return (
          <FormControl
            className='c-select'
            fullWidth
            data-testid='select-control'
            error={!!error?.[name]}
            sx={formControlStyle}
          >
            <Autocomplete
              sx={{ '& .MuiInputBase-root': { padding: '5px' } }}
              options={enableLazyLoad ? lazyMergedOptions : options}
              getOptionLabel={(option: any) => option[labelKey] || ''}
              getOptionDisabled={(option) => !!option.disabled}
              {...(enableLazyLoad
                ? {
                    open,
                    onOpen: () => setOpen(true),
                    onClose: () => {
                      setOpen(false)
                      setInputValue('')
                    },
                    inputValue,
                    onInputChange: (_e: any, newInput: string, reason: string) => {
                      if (reason !== 'reset') setInputValue(newInput)
                    },
                    filterOptions: (opts: any[]) => opts,
                    loading: lazyLoading,
                    noOptionsText: lazyNoOptionsText,
                  }
                : {})}
              isOptionEqualToValue={(option, value) => {
                if (transformToBoolean) {
                  return transform(option[valueKey]) === transform(value[valueKey])
                }
                return option[valueKey] === value[valueKey]
              }}
              value={
                (enableLazyLoad ? lazyMergedOptions : options).find((opt) => {
                  const optVal = opt[valueKey]
                  if (transformToBoolean) {
                    return (
                      transform(optVal) === transform(field.value) ||
                      transform(optVal) === transform(field.value?.[0]?.key)
                    )
                  }
                  return optVal === field.value || optVal === field.value?.[0]?.key
                }) || null
              }
              onChange={(_event, newValue) => {
                let selectedOption: string | boolean | undefined = newValue
                  ? newValue[valueKey]
                  : ''
                if (transformToBoolean) {
                  selectedOption = transform(selectedOption)
                }
                field.onChange(selectedOption)
                if (onChange)
                  onChange({ target: { name, value: selectedOption }, selectedOption: newValue })
              }}
              ListboxProps={{
                onScroll: enableLazyLoad ? handleLazyScroll : undefined,
                style: searchOnly
                  ? {
                      maxHeight: '200px',
                      padding: '0',
                    }
                  : {},
              }}
              componentsProps={{
                popper: {
                  sx: searchOnly
                    ? {
                        '& .MuiPaper-root': {
                          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                          borderRadius: '8px',
                          marginTop: '8px',
                        },
                        '& .MuiAutocomplete-option': {
                          fontSize: '13px',
                          fontWeight: '600',
                          padding: '10px 16px',
                          '&:hover': {
                            backgroundColor: '#f8f9fa',
                          },
                        },
                      }
                    : {},
                },
              }}
              renderOption={(props, option) => {
                const statusValue = statusKey ? option?.[statusKey] : undefined
                const isInactive =
                  typeof statusValue === 'string' && statusValue.toUpperCase() === 'INACTIVE'
                return (
                  <li
                    {...props}
                    style={{
                      ...(searchOnly ? { padding: '8px 16px', fontSize: '14px' } : {}),
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <span>{option?.[labelKey] ?? option}</span>
                    {statusValue !== undefined && (
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          marginLeft: 8,
                          flexShrink: 0,
                        }}
                      >
                        {statusLabel && (
                          <span style={{ fontSize: '0.75rem', color: '#6B7280' }}>
                            {statusLabel} •
                          </span>
                        )}
                        <ChipButton
                          type={isInactive ? 'secondary' : 'success'}
                          label={isInactive ? 'Inactive' : 'Active'}
                          style={{ padding: '0px 8px' }}
                        />
                      </span>
                    )}
                  </li>
                )
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={placeholder}
                  label={
                    searchOnly ? null : (
                      <span>
                        {label}
                        {required && <span style={{ color: '#D4351C' }}>*</span>}
                      </span>
                    )
                  }
                  error={!!error?.[name]}
                  helperText={error?.[name]?.message as string}
                  InputLabelProps={{
                    ...params.InputLabelProps,
                    shrink: true,
                  }}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        {startIcon}
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                  sx={textFieldStyle}
                  defaultValue={options?.length > 0 ? options?.[defaultValueIndex]?.value : ''}
                />
              )}
              disabled={disabled}
              popupIcon={searchOnly ? null : <KeyboardArrowDownIcon />}
            />
          </FormControl>
        )
      }}
    />
  )
}

export default MUIAutocompleteSingleSelect
